import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { RouteError, RouteNotFound } from "@/components/route-error";
import { openUpgrade } from "@/lib/upgrade-store";
import { useAuth } from "@/hooks/use-auth";
import { AccountChip } from "@/components/account-chip";
import { Mail, Users, ShieldCheck, Sparkles, Loader2, Sun, Moon, RotateCcw, Check, ChevronRight, ChevronDown, LogOut, Mic2, Sparkle, Target, Minus, Plus, BellRing, PauseCircle, Timer, Plane, History } from "lucide-react";
import { openAddDrawer } from "@/lib/add-drawer-store";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { regenerateForMe, getProfile, resetOnboarding, setDailyTouchTarget, setEmailPreferences } from "@/lib/relationships.functions";
import { PLANS, planOf, clampDailyTouches, type PlanId } from "@/lib/plans";
import { getMonitoringStats } from "@/lib/signals.functions";
import { getSignalPreferences, updateSignalMuteKinds, updateStandingGoal } from "@/lib/signals.functions";
import { toast } from "sonner";
import { useEffect, useState, type ReactNode } from "react";
import { useTheme } from "@/hooks/use-theme";
import { ToneTrainer, useToneSampleCount } from "@/components/tone-trainer";
import { PrivacyDashboard } from "@/components/privacy-dashboard";
import { PushNotificationsCard } from "@/components/push-notifications-card";
import { InterestTopics } from "@/components/interest-topics";
import { ScanHealthCard, useScanHealthSummary } from "@/components/scan-health-card";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { GoogleConnectionRow } from "@/components/google-connection-row";
import { SyncHistoryPanel, useSyncHistorySummary } from "@/components/sync-history-panel";
import { SecuritySettings } from "@/components/security-settings";
import { FeedbackRow } from "@/components/feedback-sheet";
import { MemoRetentionControl } from "@/components/memo-retention-control";
import { useActiveTrips } from "@/components/travel/trip-strip";

/** Travel mode lives on Today when a trip is live; this is the way in and out. */
function TravelRow() {
  const navigate = useNavigate();
  const trips = useActiveTrips(true);
  const next = trips[0];
  return (
    <Row
      icon={Plane}
      label="Travel"
      onClick={() => navigate({ to: "/travel" })}
      trailing={
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          {next ? `${next.city} · ${next.contact_count} in town` : "No trips planned"}
          <ChevronRight className="h-4 w-4" />
        </span>
      }
    />
  );
}

export const Route = createFileRoute("/_authenticated/settings")({
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound, component: Settings });

function formatRelative(iso: string, now: number) {
  const diff = Math.max(0, now - new Date(iso).getTime());
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleString();
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <h2 className="text-[15px] font-semibold text-foreground/90 px-1 mb-2">{children}</h2>;
}

function Group({ children }: { children: ReactNode }) {
  return (
    <div className="glass-soft rounded-2xl divide-y divide-border/60 overflow-hidden">
      {children}
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  trailing,
  onClick,
  destructive,
  disabled,
}: {
  icon?: any;
  label: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  destructive?: boolean;
  disabled?: boolean;
}) {
  const cls = `flex items-center gap-3 px-4 h-12 text-sm w-full text-left transition-colors ${onClick && !disabled ? "hover:bg-foreground/[0.04]" : ""} ${destructive ? "text-destructive" : ""} ${disabled ? "opacity-60" : ""}`;
  const content = (
    <>
      {Icon && <Icon className={`h-4 w-4 shrink-0 ${destructive ? "text-destructive" : "text-muted-foreground"}`} />}
      <span className="truncate">{label}</span>
      <span className="ml-auto flex items-center gap-2 text-[14px] text-muted-foreground shrink-0">{trailing}</span>
    </>
  );
  if (onClick) {
    return <button type="button" onClick={onClick} disabled={disabled} className={cls}>{content}</button>;
  }
  return <div className={cls}>{content}</div>;
}

function ExpandableRow({
  icon: Icon,
  label,
  hint,
  children,
  defaultOpen = false,
}: {
  icon?: any;
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-3 px-4 h-12 text-sm w-full text-left transition-colors hover:bg-foreground/[0.04]"
      >
        {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="truncate">{label}</span>
        <span className="ml-auto flex items-center gap-2 text-[14px] text-muted-foreground shrink-0">
          {hint}
          <ChevronDown className={"h-4 w-4 transition-transform " + (open ? "rotate-180" : "")} />
        </span>
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}

function PlanPill({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-primary/15 px-2.5 py-0.5 text-[12px] font-medium text-primary ring-1 ring-primary/25">
      {label}
    </span>
  );
}

function DailyTargetRow({
  plan,
  value,
  onChange,
  saving,
}: {
  plan: PlanId;
  value: number;
  onChange: (next: number) => void;
  saving: boolean;
}) {
  const planDef = planOf(plan);
  const max = planDef.maxDailyTouches;
  const locked = max <= 1;
  const atTop = value >= max;
  return (
    <div className="px-4 py-3.5">
      <div className="flex items-center gap-3">
        <Target className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-sm">Reach-outs per day</p>
          <p className="text-[13px] text-muted-foreground">
            How many people Today asks you to reach out to.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            aria-label="Fewer per day"
            disabled={locked || saving || value <= 1}
            onClick={() => onChange(value - 1)}
            className="grid h-8 w-8 place-items-center rounded-full glass-soft disabled:opacity-40"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="min-w-5 text-center text-sm tabular-nums">{value}</span>
          <button
            type="button"
            aria-label="More per day"
            disabled={locked || saving || atTop}
            onClick={() => onChange(Math.min(max, value + 1))}
            className="grid h-8 w-8 place-items-center rounded-full glass-soft disabled:opacity-40"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>
      {locked && (
        <p className="mt-2 pl-[30px] text-[13px] text-muted-foreground">
          Free includes 1 a day.{" "}
          <button
            type="button"
            onClick={() => openUpgrade("daily-touches")}
            className="text-primary underline-offset-2 hover:underline"
          >
            Pro unlocks up to 5
          </button>
          .
        </p>
      )}
    </div>
  );
}

function EmailToggleRow({
  icon: Icon,
  label,
  hint,
  value,
  onChange,
  saving,
  muted,
}: {
  icon: any;
  label: string;
  hint: string;
  value: boolean;
  onChange: (next: boolean) => void;
  saving: boolean;
  muted?: boolean;
}) {
  return (
    <div className={"px-4 py-3.5 " + (muted ? "opacity-55" : "")}>
      <div className="flex items-center gap-3">
        <Icon className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-sm">{label}</p>
          <p className="text-[13px] text-muted-foreground">{hint}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={value}
          aria-label={label}
          disabled={saving}
          onClick={() => onChange(!value)}
          className={
            "relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 " +
            (value ? "bg-primary" : "bg-foreground/20")
          }
        >
          <span
            className={
              "inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform " +
              (value ? "translate-x-[18px]" : "translate-x-0.5")
            }
          />
        </button>
      </div>
    </div>
  );
}

const PAUSE_CHOICES = [
  { days: 7, label: "1 week" },
  { days: 30, label: "1 month" },
];

/** Hold optional email without losing the individual switches underneath. */
function EmailPauseRow({
  pausedUntil,
  onPause,
  saving,
}: {
  pausedUntil: string | null;
  onPause: (days: number) => void;
  saving: boolean;
}) {
  const active = !!pausedUntil && new Date(pausedUntil).getTime() > Date.now();
  return (
    <div className="px-4 py-3.5">
      <div className="flex items-center gap-3">
        <PauseCircle className="h-[18px] w-[18px] shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-sm">Pause optional email</p>
          <p className="text-[13px] text-muted-foreground">
            {active
              ? `Paused until ${new Date(pausedUntil!).toLocaleDateString()}. Account email still arrives.`
              : "Quiet for a while without changing the switches above."}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {active ? (
            <button
              type="button"
              disabled={saving}
              onClick={() => onPause(0)}
              className="rounded-full bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground disabled:opacity-60"
            >
              Resume
            </button>
          ) : (
            PAUSE_CHOICES.map((c) => (
              <button
                key={c.days}
                type="button"
                disabled={saving}
                onClick={() => onPause(c.days)}
                className="rounded-full glass-soft px-3 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60"
              >
                {c.label}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Settings() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const regen = useServerFn(regenerateForMe);
  const fetchProfile = useServerFn(getProfile);
  const saveTarget = useServerFn(setDailyTouchTarget);
  const saveEmailPrefs = useServerFn(setEmailPreferences);
  const resetFn = useServerFn(resetOnboarding);
  const fetchMonitoringStats = useServerFn(getMonitoringStats);
  const qc = useQueryClient();
  const { data: profile } = useQuery({ queryKey: ["profile"], queryFn: () => fetchProfile() });
  const { data: monitoring } = useQuery({
    queryKey: ["monitoring-stats"],
    queryFn: () => fetchMonitoringStats(),
    staleTime: 60 * 60 * 1000,
  });
  const toneCount = useToneSampleCount();
  const storageKey = user?.id ? `aria:lastGeneratedAt:${user.id}` : null;
  const [lastGeneratedAt, setLastGeneratedAt] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!storageKey) return;
    setLastGeneratedAt(localStorage.getItem(storageKey));
  }, [storageKey]);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const regenMut = useMutation({
    mutationFn: () => regen(),
    onSuccess: ({ generated }) => {
      qc.invalidateQueries({ queryKey: ["today"] });
      const iso = new Date().toISOString();
      setLastGeneratedAt(iso);
      if (storageKey) localStorage.setItem(storageKey, iso);
      if (generated > 0) {
        toast.success(
          generated === 1 ? "1 new suggestion added to Today" : `${generated} new suggestions added to Today`,
          { description: "Open Today to see who's up." },
        );
      } else {
        toast("No new suggestions right now", { description: "You're all caught up." });
      }
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't refresh"),
  });
  const resetMut = useMutation({
    mutationFn: (wipeData: boolean) => resetFn({ data: { wipeData } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Onboarding reset — redirecting…");
      navigate({ to: "/onboarding" });
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't reset"),
  });
  const tier: PlanId = planOf((profile as any)?.plan).id;
  const tierPlan = planOf(tier);
  const tierLabel = tierPlan.name;
  // A granted plan with an end date is founding/trial access — show the clock
  // so the upgrade conversation is expected rather than a surprise.
  const planExpiresAt: string | null = (profile as any)?.plan_expires_at ?? null;
  const trialEnds =
    planExpiresAt && tier !== "free" && new Date(planExpiresAt).getTime() > Date.now()
      ? new Date(planExpiresAt)
      : null;
  const savedTarget = clampDailyTouches((profile as any)?.daily_touch_target ?? tierPlan.defaultDailyTouches, tier);
  const hasUpgrade = PLANS.some((p) => !p.disabled && p.id !== tier && PLANS.indexOf(p) > PLANS.findIndex((x) => x.id === tier));
  const [pendingTarget, setPendingTarget] = useState<number | null>(null);
  const targetMut = useMutation({
    mutationFn: (target: number) => saveTarget({ data: { target } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["today"] });
      setPendingTarget(null);
    },
    onError: (e: any) => {
      setPendingTarget(null);
      toast.error(e?.message ?? "Couldn't save");
    },
  });
  const dailyTarget = pendingTarget ?? savedTarget;
  // Email preferences: optimistic locally, authoritative from the profile.
  type EmailPatch = {
    weeklyBrief?: boolean;
    signalAlerts?: boolean;
    productTips?: boolean;
    pauseDays?: number;
  };
  const [pendingEmail, setPendingEmail] = useState<EmailPatch>({});
  const emailMut = useMutation({
    mutationFn: (patch: EmailPatch) => saveEmailPrefs({ data: patch }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["profile"] });
      setPendingEmail({});
    },
    onError: (e: any) => {
      setPendingEmail({});
      toast.error(e?.message ?? "Couldn't save");
    },
  });
  const applyEmail = (patch: EmailPatch) => {
    setPendingEmail((prev) => ({ ...prev, ...patch }));
    emailMut.mutate(patch);
  };
  const prof = profile as any;
  const retentionDays: number = prof?.memo_retention_days ?? 0;
  const weeklyEmail = pendingEmail.weeklyBrief ?? prof?.weekly_email_enabled !== false;
  const signalAlerts = pendingEmail.signalAlerts ?? prof?.email_signal_alerts !== false;
  const productTips = pendingEmail.productTips ?? prof?.email_product_tips !== false;
  const pausedUntil =
    pendingEmail.pauseDays !== undefined
      ? pendingEmail.pauseDays === 0
        ? null
        : new Date(Date.now() + pendingEmail.pauseDays * 86400_000).toISOString()
      : (prof?.email_paused_until ?? null);
  const emailPaused = !!pausedUntil && new Date(pausedUntil).getTime() > Date.now();
  const emailSaving = emailMut.isPending;
  const { resolvedTheme, setTheme } = useTheme();
  const isLight = resolvedTheme === "light";
  const isAdmin = useIsAdmin();
  const scanHealth = useScanHealthSummary();
  const syncSummary = useSyncHistorySummary();
  const emailSummary = emailPaused
    ? `Paused until ${new Date(pausedUntil!).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
    : [weeklyEmail && "Sunday Brief", signalAlerts && "Deep Watch", productTips && "Tips"]
        .filter(Boolean)
        .join(", ") || "All off";
  const scanSummary = scanHealth.loading
    ? "Checking…"
    : scanHealth.warmingUp && scanHealth.attention === 0
      ? "Starting up"
      : scanHealth.attention > 0
        ? `${scanHealth.attention} need attention`
        : `All scans running · ${scanHealth.total}`;
  const themeToggle = (
    <span
      aria-pressed={isLight}
      className={
        "relative inline-flex h-6 w-10 items-center rounded-full transition-colors " +
        (isLight ? "bg-primary" : "bg-foreground/15")
      }
    >
      <span
        className={
          "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform " +
          (isLight ? "translate-x-[18px]" : "translate-x-0.5")
        }
      />
    </span>
  );

  return (
    <div className="space-y-5 pb-16">
      <header className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
          <p className="mt-1 truncate text-sm text-muted-foreground">{user?.email}</p>
        </div>
        <div className="hidden shrink-0 md:block">
          <AccountChip />
        </div>
      </header>

      <div>
        <SectionLabel>Account</SectionLabel>
        <Group>
          <ExpandableRow
            icon={Sparkle}
            label="Plan"
            hint={
              <span className="inline-flex items-center gap-2">
                <PlanPill label={tierLabel} />
                {trialEnds && (
                  <span className="text-[12px] text-muted-foreground">
                    until {trialEnds.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                )}
              </span>
            }
          >
            <ul className="space-y-2">
              {trialEnds ? (
                <li className="rounded-2xl glass-soft px-3 py-2.5">
                  <p className="text-sm">Founding access</p>
                  <p className="text-[13px] text-muted-foreground">
                    {tierLabel} through{" "}
                    {trialEnds.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    {" — then Free, with your people intact."}
                  </p>
                </li>
              ) : null}
              {PLANS.map((t) => {
                const active = t.id === tier;
                return (
                  <li
                    key={t.id}
                    className={
                      "rounded-2xl px-3 py-2.5 flex items-start gap-3 " +
                      (active
                        ? "bg-primary/[0.07] ring-1 ring-primary/30 "
                        : "glass-soft ") +
                      (t.disabled && !active ? "opacity-60" : "")
                    }
                  >
                    <div className="flex-1 min-w-0">
                      {active && (
                        <p className="mb-0.5 text-[11px] font-medium uppercase tracking-wide text-primary">
                          Current plan
                        </p>
                      )}
                      <p className="text-sm">
                        {t.name}
                        <span className="text-muted-foreground"> · {t.priceMonthly}{t.unit ? ` ${t.unit}` : ""}</span>
                      </p>
                      <p className="text-[13px] text-muted-foreground">{t.blurb}</p>
                    </div>
                    <span className="text-[13px] shrink-0 mt-0.5">
                      {active ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : t.disabled ? (
                        <span className="text-muted-foreground">Coming soon</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openUpgrade("settings")}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          See plan
                        </button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-[12px] leading-5 text-muted-foreground">
                Billing is handled personally during founding access — no card on file.
              </p>
              {hasUpgrade && (
                <button
                  type="button"
                  onClick={() => openUpgrade("settings")}
                  className="inline-flex h-9 shrink-0 items-center rounded-full bg-primary px-3.5 text-[13px] font-medium text-primary-foreground"
                >
                  Request upgrade
                </button>
              )}
            </div>
          </ExpandableRow>
          <DailyTargetRow
            plan={tier}
            value={dailyTarget}
            saving={targetMut.isPending}
            onChange={(next) => {
              setPendingTarget(next);
              targetMut.mutate(next);
            }}
          />
          <Row
            icon={isLight ? Sun : Moon}
            label="Theme"
            onClick={() => setTheme(isLight ? "dark" : "light")}
            trailing={themeToggle}
          />
          <Row
            icon={LogOut}
            label="Sign out"
            destructive
            onClick={() => signOut().then(() => navigate({ to: "/" }))}
            trailing={<ChevronRight className="h-4 w-4" />}
          />
        </Group>
      </div>

      <div>
        <SectionLabel>Live Context</SectionLabel>
        <p className="px-1 pb-2 text-[13px] leading-snug text-muted-foreground">
          Your calendar gives Pyng prep before meetings and hooks after them. Gmail is read as
          metadata only — who and when, never message contents — so your last touch is real.
        </p>
        <Group>
          <GoogleConnectionRow connectorId="google_calendar" locked={!tierPlan.liveContext} />
          <GoogleConnectionRow connectorId="google_mail" locked={!tierPlan.liveContext} />
          <Row
            icon={Users}
            label="Import from Google Contacts"
            trailing={<span className="text-[13px]">One-time import</span>}
            onClick={() => openAddDrawer("person")}
          />
        </Group>
        <Group>
          <ExpandableRow
            icon={History}
            label="Sync history"
            hint={
              <span className={syncSummary.ok ? "text-muted-foreground" : "text-amber-500"}>
                {syncSummary.label}
              </span>
            }
          >
            <div className="-mx-4">
              <SyncHistoryPanel bare />
            </div>
          </ExpandableRow>
        </Group>
      </div>

      <div>
        <SectionLabel>Signals</SectionLabel>
        <Group>
          <TravelRow />
          <ExpandableRow icon={Sparkle} label="What you're working toward" hint="Biases which news is worth a Pyng">
            <StandingGoalPicker />
          </ExpandableRow>
          <ExpandableRow icon={Sparkle} label="Signal types" hint="What earns a Pyng">
            <SignalMuteToggles />
          </ExpandableRow>
          <ExpandableRow icon={Sparkles} label="Interests & topics" hint="Watched across your circle">
            <InterestTopics />
          </ExpandableRow>
          <Row
            icon={Sparkles}
            label="Refresh suggestions"
            trailing={
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (!regenMut.isPending) regenMut.mutate(); }}
                disabled={regenMut.isPending}
                className="inline-flex h-10 items-center gap-1.5 rounded-full bg-primary px-3 text-[13px] font-medium text-primary-foreground disabled:opacity-50"
              >
                {regenMut.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                {regenMut.isPending ? "Thinking…" : "Generate"}
              </button>
            }
          />
          {lastGeneratedAt && (
            <Row icon={Sparkles} label={<span className="text-muted-foreground">Last generated</span>} trailing={formatRelative(lastGeneratedAt, now)} />
          )}
          <Row
            icon={Sparkle}
            label={<span className="text-muted-foreground">Monitoring daily</span>}
            trailing={`${(monitoring as any)?.peopleMonitored ?? 0} people`}
          />
        </Group>
      </div>

      <div>
        <SectionLabel>Email</SectionLabel>
        <Group>
          <ExpandableRow icon={Mail} label="Email from Pyng" hint={emailSummary}>
            <div className="-mx-4">
              <EmailToggleRow
                icon={Mail}
                label="The Sunday Brief"
                hint="A Sunday recap and your reasons to reach out this week."
                value={weeklyEmail}
                muted={emailPaused}
                saving={emailSaving}
                onChange={(next) => applyEmail({ weeklyBrief: next })}
              />
              <EmailToggleRow
                icon={BellRing}
                label="Deep Watch alerts"
                hint="An email the moment something big lands for a person you star."
                value={signalAlerts}
                muted={emailPaused}
                saving={emailSaving}
                onChange={(next) => applyEmail({ signalAlerts: next })}
              />
              <EmailToggleRow
                icon={Sparkles}
                label="Tips and product notes"
                hint="Occasional guidance on getting more out of Pyng."
                value={productTips}
                muted={emailPaused}
                saving={emailSaving}
                onChange={(next) => applyEmail({ productTips: next })}
              />
              <EmailPauseRow
                pausedUntil={pausedUntil}
                saving={emailSaving}
                onPause={(days) => applyEmail({ pauseDays: days })}
              />
            </div>
            <p className="mt-2 text-[12px] leading-5 text-muted-foreground">
              Account and access email — sign-in, approval, and anything you need to reach your
              account — always sends, so these switches can never lock you out.
            </p>
          </ExpandableRow>
        </Group>
      </div>

      <div>
        <SectionLabel>Daily scan health</SectionLabel>
        <Group>
          <ExpandableRow
            icon={Sparkle}
            label="Signal Engine™"
            hint={
              <span className={scanHealth.attention > 0 ? "text-amber-500" : "text-[var(--accent-neon-ink)]"}>
                {scanSummary}
              </span>
            }
          >
            <div className="-mx-4">
              <ScanHealthCard />
            </div>
          </ExpandableRow>
        </Group>
      </div>

      <div>
        <SectionLabel>Notifications</SectionLabel>
        <Group>
          <div className="px-4 py-3">
            <PushNotificationsCard compact />
          </div>
        </Group>
      </div>

      <div>
        <SectionLabel>Voice</SectionLabel>
        <Group>
          <ExpandableRow
            icon={Mic2}
            label="Teach Pyng your voice"
            hint={toneCount > 0 ? `${toneCount} learned` : "Not started"}
          >
            <ToneTrainer embedded />
          </ExpandableRow>
          <ExpandableRow
            icon={Timer}
            label="Voice memo retention"
            hint={
              retentionDays === 0 ? "Keep until deleted" : `Auto-delete after ${retentionDays} days`
            }
          >
            <MemoRetentionControl
              value={retentionDays}
              onChanged={() => qc.invalidateQueries({ queryKey: ["profile"] })}
            />
          </ExpandableRow>
        </Group>
      </div>

      <div>
        <SectionLabel>Feedback</SectionLabel>
        <FeedbackRow />
      </div>

      <div>
        <SectionLabel>Privacy &amp; data</SectionLabel>
        <Group>
          <ExpandableRow icon={ShieldCheck} label="Your data" hint="Export · Delete">
            <PrivacyDashboard embedded />
          </ExpandableRow>
        </Group>
      </div>

      <div>
        <SectionLabel>Security</SectionLabel>
        <SecuritySettings />
      </div>

      {isAdmin && (
        <div>
          <SectionLabel>Developer</SectionLabel>
          <Group>
            <Row
              icon={RotateCcw}
              label="Re-run onboarding"
              onClick={() => !resetMut.isPending && resetMut.mutate(false)}
              disabled={resetMut.isPending}
              trailing={<ChevronRight className="h-4 w-4" />}
            />
            <Row
              icon={RotateCcw}
              label="Wipe data + re-run"
              destructive
              onClick={() => {
                if (resetMut.isPending) return;
                if (confirm("Wipe all contacts, memories, hooks, and suggestions, then re-run onboarding?")) {
                  resetMut.mutate(true);
                }
              }}
              disabled={resetMut.isPending}
              trailing={resetMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
            />
          </Group>
        </div>
      )}
    </div>
  );
}

const SIGNAL_KIND_META: { id: string; label: string; blurb: string }[] = [
  {
    id: "role_change",
    label: "Role changes",
    blurb: "New job, promotion, title change, lateral move, stepping down.",
  },
  { id: "launch", label: "Launches", blurb: "Products, projects, public releases." },
  { id: "press", label: "Press", blurb: "Interviews, articles, mentions." },
  { id: "milestone", label: "Milestones", blurb: "Funding, awards, big personal news." },
  { id: "post", label: "Posts", blurb: "Public social posts and blog updates." },
];

/** The standing goal from onboarding, editable. Same options, same mapping. */
const GOAL_OPTIONS: Array<{ value: string; label: string; hint: string }> = [
  { value: "founder", label: "Raising or building", hint: "Funding rounds, exits, investor moves" },
  { value: "sales", label: "Winning business", hint: "Budget shifts, new leaders, expansion" },
  { value: "recruiting", label: "Hiring or being hired", hint: "Role changes, team growth, departures" },
  { value: "investor", label: "Finding and backing companies", hint: "Launches, traction, funding news" },
  { value: "banking", label: "Advising on deals", hint: "M&A, strategic reviews, activists" },
  { value: "general", label: "Just staying close", hint: "Anything meaningful to the people I know" },
];

function StandingGoalPicker() {
  const fetchPrefs = useServerFn(getSignalPreferences);
  const saveGoal = useServerFn(updateStandingGoal);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["signal-prefs"],
    queryFn: () => fetchPrefs(),
    staleTime: 5 * 60 * 1000,
  });
  const current = ((data as any)?.focus as string | undefined) ?? "general";
  const mut = useMutation({
    mutationFn: (focus: string) => saveGoal({ data: { focus } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["signal-prefs"] });
      toast.success("Saved — new signals will be weighted for this.");
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't save"),
  });
  if (isLoading) return <p className="text-[12px] text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-2">
      <p className="-mt-1 text-[13px] text-muted-foreground">
        Pyng still watches everything — this decides what gets to interrupt you.
      </p>
      {GOAL_OPTIONS.map((g) => {
        const active = current === g.value;
        return (
          <button
            key={g.value}
            type="button"
            onClick={() => !mut.isPending && mut.mutate(g.value)}
            disabled={mut.isPending}
            className={
              "flex min-h-11 w-full items-center justify-between gap-3 rounded-2xl px-3 py-2.5 text-left transition " +
              (active
                ? "bg-[var(--accent-neon)]/10 ring-1 ring-[var(--accent-neon)]/60"
                : "hover:bg-white/5")
            }
          >
            <span className="min-w-0">
              <span className="block text-sm">{g.label}</span>
              <span className="block text-[13px] text-muted-foreground">{g.hint}</span>
            </span>
            {active && <Check className="h-4 w-4 shrink-0 text-[var(--accent-neon)]" />}
          </button>
        );
      })}
    </div>
  );
}

function SignalMuteToggles() {
  const fetchPrefs = useServerFn(getSignalPreferences);
  const updatePrefs = useServerFn(updateSignalMuteKinds);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["signal-prefs"],
    queryFn: () => fetchPrefs(),
    staleTime: 5 * 60 * 1000,
  });
  const muted = new Set<string>(((data as any)?.mutedKinds ?? []) as string[]);
  const mut = useMutation({
    mutationFn: (mutedKinds: string[]) => updatePrefs({ data: { mutedKinds: mutedKinds as any } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["signal-prefs"] }),
    onError: (e: any) => toast.error(e?.message ?? "Couldn't save"),
  });
  const toggle = (kind: string) => {
    const next = new Set(muted);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    mut.mutate(Array.from(next));
  };
  if (isLoading) return <p className="text-[12px] text-muted-foreground">Loading…</p>;
  return (
    <ul className="space-y-2">
      <li className="text-[13px] text-muted-foreground -mt-1">
        Turn off types you don't want to see in Today or person history.
      </li>
      {SIGNAL_KIND_META.map((k) => {
        const isMuted = muted.has(k.id);
        const isActive = !isMuted;
        return (
          <li key={k.id} className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm">{k.label}</p>
              <p className="text-[13px] text-muted-foreground">{k.blurb}</p>
            </div>
            <button
              type="button"
              onClick={() => toggle(k.id)}
              disabled={mut.isPending}
              aria-pressed={isActive}
              className={
                "relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors mt-0.5 " +
                (isActive ? "bg-primary" : "bg-foreground/15")
              }
            >
              <span
                className={
                  "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform " +
                  (isActive ? "translate-x-[18px]" : "translate-x-0.5")
                }
              />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
